import { $formats, UIParagraphFormat } from '../../config/formats';
import { UIIcon } from '../../config/icons';
import { $vars } from '../../config/vars';
import { sUploadImage } from '../mdl/s-upload';
import { uTranslateText } from '../mdl/u-translate';
import { onDetach } from '../ui/attach';
import { UIButton } from '../ui/component/button';
import { UIDialog } from '../ui/component/dialog';
import { UIDropdown, valueDropdownGenerator } from '../ui/component/dropdown';
import { UIFieldTag, UIImageTag, registerFieldEditor } from '../ui/component/field-tag';
import { UITextInput } from '../ui/component/input';
import { UILabeled } from '../ui/component/labeled';
import { UISelect } from '../ui/component/select';
import { UIExclusiveToggles, UIRadio } from '../ui/component/toggle';
import { UIToolbar } from '../ui/component/toolbar';
import { UIContent, UIDropDelegate, isImageMime, uiInitDropTarget } from '../ui/dnd';
import { $el } from '../ui/elements';
import { uiGetPrefixedClass } from '../ui/functions';
import { UIKey, onKey } from '../ui/key';
import { UIMenu, UIMenuItem, UISelectMenuItem } from '../ui/menu';
import { uiHidePopup, uiShowPopup } from '../ui/popup';
import { UIParagraphStyleParam, UIParagraphStyleTypes, UITextStyleParam, UITextStyleTypes } from '../ui/style';
import { UIClassesSpec, UIRelPos, UIValue } from '../ui/types';
import { UIView, UIViewAddSpec, UIViewTag } from '../ui/view';
import { $log } from '../y/debug';
import { $on, $trigger, YBlurEvent, YEventHandlers, YFocusEvent, YInputEvent, YStateEvent, YValueEvent } from '../y/events';
import { $fn } from '../y/fn';
import { $form, YInput, YInputOptions } from '../y/form';
import { $i18n, YI18NString, YLanguage, yGetI18NIn } from '../y/i18n';
import { yCopy, yEqual } from '../y/object';
import { YId } from '../y/types';
import { EDFields } from './fields';
import { EDSelection, getSelection, select } from './selection';

interface InsertLinkResult {
    url?: string;
    link?: string;
}

class InsertLinkDialog extends UIDialog<InsertLinkResult> {
    constructor(link_values?: UIValue<string>[]) {
        super();
        this.classList.add('m-flexible-460');

        const type_values = [{ key: 'url', title: 'URL' }, { key: 'link', title: 'Predefined' }];
        const type_group = new UIExclusiveToggles(type_values, v => new UIRadio(v), { classes: 'l-toggle-row' });

        const url = new UITextInput({ autofocus: true, kind: 'k-subtle' });
        const link = new UISelect(link_values || [], { kind: 'k-subtle' });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Insert link'),
            link_values && type_group,
            new UILabeled(url, { above: 'URL' }),
            new UILabeled(link, { above: 'Link' }),
            $el.DIV('s-small'),
            $el.DIV('l-button-row', [
                new UIButton({ title: $i18n.ui.button_cancel, classes: 'm-hollow', on_click: () => { this.dismiss(null) } }),
                new UIButton({
                    title: "Insert", on_click: () => {
                        if(type_group.value == 'url') {
                            this.dismiss({ url: url.value });
                        } else {
                            this.dismiss({ link: link.value });
                        }
                    }
                })
            ])
        ]));

        link.ui_hide();
        type_group.value = 'url';

        $on(type_group, 'value', v => {
            url.ui_show(undefined, v == 'url');
            link.ui_show(undefined, v == 'link');
        });
    }
}
let toolbar: UIToolbar | undefined;
let menu: UIMenu | undefined;
let style_menu_item: UISelectMenuItem<UIParagraphFormat> | undefined;
let current_editable: InputBase<any> | undefined;

function getSelectionAsDiv() {
    const selection = window.getSelection()!;

    if(selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const clonedSelection = range.cloneContents();
        const div = $el.DIV();
        div.appendChild(clonedSelection);
        return div;
    }

    return;
}

let copied_text: string | undefined;
let copied_html: string | undefined;

function copyCutHandler(ev: ClipboardEvent) {
    const div = getSelectionAsDiv();

    $log('editable', 'copying')
    if(div) {
        copied_text = div.textContent || undefined;
        copied_html = div.innerHTML;
        $log('editable', 'good', copied_text, copied_html);
        if(copied_text) {
            ev.clipboardData?.setData('text/html', copied_html);
            ev.clipboardData?.setData('text/plain', copied_text || '');
            ev.preventDefault();
        }
    } else {
        copied_text = undefined;
    }
}

function pasteHandler(ev: ClipboardEvent) {
    $log('editable', 'paste');
    if(!ev || !ev.clipboardData) return;
    const html = ev.clipboardData.getData('text/html');
    const text = ev.clipboardData.getData('text/plain');
    ev.preventDefault();
    $log('editable', 'pipi', html, text, copied_text);
    if(html && text && text === copied_text) {
        $log('editable', 'inserting as html');
        document.execCommand('insertHTML', false, copied_html)
    } else if(text) {
        copied_text = undefined;
        document.execCommand('insertText', false, text);
    }
}

function createMenu() {
    if(menu) return;

    function makeStyleCheckItem<P extends UITextStyleParam>(icon: UIIcon, title: string, style: P, value: UITextStyleTypes[P]): UIMenuItem {
        return { icon, title, get checked() { return current_editable?.hasTextStyle(style, value) || false }, set checked(v) { current_editable?.setTextStyle(style, v ? value : undefined) } };
    }

    function makeAlignCheckItem(icon: UIIcon, title: string, value: UIParagraphStyleTypes['a']): UIMenuItem {
        return { icon, title, get checked() { return current_editable?.hasParagraphStyle('a', value) || false }, set checked(v: boolean) { if(v) { current_editable?.setParagraphStyle('a', value); menu?.update() } } };
    }

    const items: UIMenuItem[] = [
        makeStyleCheckItem(UIIcon.BOLD, 'Bold', 'w', 'bold'),
        makeStyleCheckItem(UIIcon.ITALIC, 'Italic', 's', 'italic'),
        makeStyleCheckItem(UIIcon.UNDERLINE, 'Underline', 'underline', true),
        makeStyleCheckItem(UIIcon.STRIKETHROUGH, 'Strikethrough', 'strike', true),
        {
            select: [{ key: 'fg', name: 'Normal' }, { key: 'soft', name: 'Soft' }, { key: 'hard', name: 'Hard' }, { key: 'tint', name: 'Tint' }, { key: 'secondary', name: 'Secondary' }],
            display: (value: string) => $el.DIV(['ui-color-circle', 't-c-' + value]),
            get value() { return current_editable?.getTextStyle('c') || 'fg' },
            set value(v) { current_editable?.setTextStyle('c', v); menu?.update(); }
        },
        // { icon: UIIcon.ROTATE_LEFT, title: 'Undo', action: () => { current_editable?.selection?.undo() }},
        // { icon: UIIcon.ROTATE_RIGHT, title: 'Redo', action: () => { current_editable?.selection?.redo() }},
        { icon: UIIcon.LINK, title: 'Insert link', action: () => { current_editable?.insertLink() } },
        {
            icon: UIIcon.LIST_UL,
            title: 'List',
            get checked() { return current_editable?.hasParagraphStyle('l', '1') || false },
            set checked(v: boolean) { current_editable?.setParagraphStyle('l', v ? '1' : undefined); menu?.update() }
        },
        makeAlignCheckItem(UIIcon.ALIGN_LEFT, 'Align left', 'left'),
        makeAlignCheckItem(UIIcon.ALIGN_CENTER, 'Align center', 'center'),
        makeAlignCheckItem(UIIcon.ALIGN_RIGHT, 'Align right', 'right'),
        makeAlignCheckItem(UIIcon.ALIGN_JUSTIFY, 'Align justify', 'justify'),
        style_menu_item = {
            select: [],
            get value() { return current_editable?.getParagraphStyle('f') || 'normal' },
            set value(v) { current_editable?.setParagraphStyle('f', v); menu?.update(); }
        }
    ];

    menu = new UIMenu(items);
    menu.hidden = true;
}

export function getTextMenu() {
    if(!menu) createMenu();
    return menu!;
}

interface ToolbarOptions {
    text?: boolean;
    alignment?: boolean;
    formats?: UIParagraphFormat[];
}

function showMenu(editable: InputBase<any>, options: ToolbarOptions = {}, show_toolbar: boolean) {
    if(!menu) createMenu();

    current_editable = editable;

    if(options.formats) {
        style_menu_item!.select = options.formats.map(f => ({ key: f, name: $formats[f].name }));
        style_menu_item!.when = true;
    } else {
        style_menu_item!.when = false;
    }

    menu!.hidden = false;

    if(show_toolbar) {
        toolbar ??= new UIToolbar(menu!);
        uiShowPopup(toolbar, editable, { position: UIRelPos.Above, offset: 12, prevent_blur: true });
    }
}

function hideMenu(editable: InputBase<any>) {
    if(!menu) return;
    if(editable !== current_editable) return;
    current_editable = undefined;
    menu.hidden = true;

    toolbar && uiHidePopup(toolbar);
}

function fixElement(element: HTMLElement, default_format?: UIParagraphFormat) {
    $log('editable', 'fixing element');
    const before = element.innerHTML;
    const before_text = element.textContent || '';
    if(before_text.includes('\n'))
        $log('editable', 'includes \\n %o', before_text);
    if(before_text.includes('\r'))
        $log('editable', 'includes \\r');
    if(before_text.includes('\u00a0'))
        $log('editable', 'includes \\u00a0');

    const nodes = Array.from(element.childNodes);

    for(let i = 0; i < element.childNodes.length; i++) {
        const n = element.childNodes[i];
        if(n instanceof Element && n.tagName == 'DIV') {
            const classes = Array.from(n.classList).sort();
            const new_spec: UIClassesSpec = [];

            let f = (uiGetPrefixedClass(n, 'p-f') || default_format) as UIParagraphFormat | undefined;
            if(f && !$formats[f]) {
                $log('editable', '%cunknown format %s', 'color:red', f);
                f = default_format;
            }
            if(f && $formats[f].classes)
                new_spec.push($formats[f].classes);
            const a = uiGetPrefixedClass(n, 'p-a');
            if(a)
                new_spec.push('p-a-' + a);
            const l = uiGetPrefixedClass(n, 'p-l');
            if(l)
                new_spec.push('p-l-' + l);
            if(n.childNodes.length == 0)
                new_spec.push('ed-empty-paragraph');
            const new_classes = $el.splitClasses($el.flattenClasses(new_spec)).sort();
            if(!yEqual(classes, new_classes)) {
                $log('editable', 'different classes', classes.join(' '), '-->', new_classes.join(' '));
            }
            n.className = new_classes.join(' ');

            // for(const s of Array.from(n.children)) {
            //     if(s.tagName == 'UL' || s.tagName == 'DIV') {
            //         n.replaceWith(s);
            //     }
            // }
        } else {
            const e = $el.DIV(default_format && $formats[default_format].classes);
            n.replaceWith(e);
            e.appendChild(n);

            while(i + 1 < nodes.length) {
                const next = nodes[i + 1];
                if(!(next instanceof Element && (next.tagName == 'DIV' || next.tagName == 'UL'))) {
                    e.appendChild(next);
                    i++;
                } else {
                    break;
                }
            }
        }
    }

    for(const s of Array.from(element.getElementsByTagName('span'))) {
        s.removeAttribute('style');
        // s.replaceWith(...Array.from(s.childNodes))
    }

    // for(const a of Array.from(element.getElementsByTagName('a'))) {
    //     a.style.color = 'inherit';
    // }

    const after = element.innerHTML;

    if(before != after) {
        $log('editable', before);
        $log('editable', ' -> ');
        $log('editable', after);
    }
}

const deduplicated_callback = $fn.deduplicate(_ev => {
    if(!current_editable || !menu) return;
    current_editable.onSelectionChange();
    menu.update();
})

document.addEventListener('selectionchange', deduplicated_callback);

let current_bottom_toolbar: UIToolbar | undefined;

export interface EDInputOptions extends YInputOptions {
    tag?: UIViewTag;
    classes?: UIClassesSpec;

    styleable?: boolean;
    multiline?: boolean;
    images?: string;
    formats?: UIParagraphFormat[];
    default_format?: UIParagraphFormat;
    show_toolbar?: boolean;
    spellcheck?: boolean;

    placeholder?: string;

    fields?: EDFields;
    link_values?: UIValue<string>[];
    bottom_toolbar?: UIToolbar;

    autocomplete?: (term: string) => Promise<UIValue[]>;
}

const delete_variants: { [key: string]: ['backward' | 'forward', 'character' | 'word'] } = {
    'deleteContentBackward': ['backward', 'character'],
    'deleteContentForward': ['forward', 'character'],
    'deleteWordBackward': ['backward', 'word'],
    'deleteWordForward': ['forward', 'word']
}

// TODO: when it's plain text, replace <br> with \n back and forth!!
abstract class InputBase<Value> extends UIView implements YInput<Value>, UIDropDelegate {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Value> & YBlurEvent & YFocusEvent>;

    private _showing_placeholder?: boolean;
    private _placeholder?: string;
    private _fields?: EDFields;
    private _link_values?: UIValue<string>[];
    private _autocomplete?: (term: string) => Promise<UIValue[]>;
    private _autocomplete_token?: {};
    private _autocomplete_popup?: UIDropdown;
    private _autocomplete_last_text?: string;
    private _bottom_toolbar?: UIToolbar;
    private _selection?: EDSelection;
    private _styleable?: boolean;
    private _show_toolbar: boolean;
    private _image_scope?: string;
    private _default_format?: UIParagraphFormat;
    private _placeheld_value?: string;
    private _focused: boolean;

    okay: boolean;
    dirty!: boolean;

    on_empty_backspace?: () => void;
    on_tab?: () => void;
    on_enter?: () => void;
    on_autocomplete?: (v: UIValue) => void;

    protected _setValue(v: string) {
        this._showing_placeholder = false;

        // TODO: add a mode which is not only not multiline, but also doesn't have paragraphs
        //in that case this would be empty, and the root would also be a container
        this.root.innerHTML = v || '<div></div>';
        fixElement(this.root, this._default_format);

        this._updatePlaceholder();
    }

    protected _getValue() {
        if(this._showing_placeholder)
            return this._placeheld_value || '';
        return this.root.innerHTML;
    }

    private _onInput() {
        $form.setDirty(this, true);

        // TODO: consolidate this with UITextInput
        if(this._autocomplete) {
            const text = this.root.textContent || '';
            if(text !== this._autocomplete_last_text) {
                this._autocomplete_last_text = text;

                if(this._autocomplete_popup) {
                    this._autocomplete_popup.close();
                    this._autocomplete_popup = undefined;
                }

                if(text) {
                    const autocomplete = this._autocomplete;
                    // TODO: wrap this token logic in some reusable function
                    const token = this._autocomplete_token = {};
                    setTimeout(() => {
                        if(token !== this._autocomplete_token) return;
                        autocomplete(text).then((options) => {
                            if(token !== this._autocomplete_token) return;

                            const popup = new UIDropdown(options, valueDropdownGenerator)
                            popup.on_choose = v => {
                                this.on_autocomplete && this.on_autocomplete(v);
                                this._setValue(v.name);
                            };
                            this._autocomplete_popup && this._autocomplete_popup.close();
                            this._autocomplete_popup = popup;
                            popup.open(this, { position: UIRelPos.Below | UIRelPos.End, adjust_size: true, match_size: true, capture_mouse: true });
                        });
                    }, 500);
                }
            }
        }

        $trigger(this, 'input');
    }

    constructor(options: EDInputOptions = {}) {
        super(options.tag, [options.classes, 'ed-input']);
        const el = this.root;
        el.contentEditable = 'true';
        el.spellcheck = !!options.spellcheck

        this._placeholder = options.placeholder;
        this._styleable = options.styleable;
        this._show_toolbar = !!options.show_toolbar;
        this._autocomplete = options.autocomplete;
        this._bottom_toolbar = options.bottom_toolbar;
        this._default_format = options.default_format;
        this._link_values = options.link_values;
        this._focused = false;
        this.okay = true;

        registerFieldEditor(this, f => {
            f.contentEditable = 'false';
            f.addEventListener('mousedown', ev => {
                select(f);
                ev.preventDefault();
            });
        });

        onKey(el, e => {
            if(e.key === UIKey.Backspace) {
                if(!el.textContent && el.childElementCount < 2) {
                    this.on_empty_backspace && this.on_empty_backspace();
                    return true;
                }
            } else if(e.key === UIKey.Tab) {
                if(this.on_tab) {
                    this.on_tab();
                    return true;
                }
            } else if(e.key === UIKey.Enter) {
                if(!options.multiline) {
                    this.on_enter && this.on_enter();
                    return true;
                }
            }
            return;
        });

        this._setValue('');

        el.addEventListener('beforeinput', ev => {
            $log('editable', '%O %s %s %s', ev, ev.inputType, ev.data, ev.cancelable);
            if(!ev.cancelable) return;
            const selection = getSelection(this)!;
            const t = ev.inputType;

            if(t.startsWith('delete')) {
                ev.preventDefault();
                if(selection.hasContent()) {
                    selection.remove();
                } else {
                    const variant = delete_variants[t];
                    if(variant) {
                        window.getSelection()?.modify('extend', variant[0], variant[1]);
                        getSelection(this)!.remove();
                    }
                }
                fixElement(this.root, this._default_format);
            } else if(t.startsWith('format')) {
                ev.preventDefault();
                if(t == 'formatBold') {
                    $log('editable', 'manually setting bold');
                    selection.setTextStyle('w', selection.hasTextStyle('w', 'bold') ? 'regular' : 'bold');
                } else if(t == 'formatItalic') {
                    $log('editable', 'manually setting italic');
                    selection.setTextStyle('s', selection.hasTextStyle('s', 'italic') ? 'normal' : 'italic');
                } else if(t == 'formatUnderline') {
                    $log('editable', 'manually setting underline');
                    selection.setTextStyle('underline', !selection.hasTextStyle('underline', true));
                } else if(t == 'formatStrikeThrough') {
                    $log('editable', 'manually setting strikethrough');
                    selection.setTextStyle('strike', !selection.hasTextStyle('strike', true));
                } else if(t.startsWith('formatJustify')) {
                    ev.preventDefault();
                    selection.setParagraphStyle('a', t == 'formatJustifyFull' ? 'justify' : t == 'formatJustifyCenter' ? 'center' : t == 'formatJustifyRight' ? 'right' : 'left');
                }
            } else if(t.startsWith('history')) {
                ev.preventDefault();
                $log('editable', '%cnot implemented type %s', $log.BLUE, ev.inputType)
            } else {
                $log('editable', '%cunhandled input type %s', $log.ORANGE, ev.inputType)
            }
        });

        if(options.images) {
            this._image_scope = options.images;
            uiInitDropTarget(this);
        }

        el.addEventListener('input', () => {
            fixElement(this.root, this._default_format);
            this._onInput();
        });

        el.addEventListener('focus', ev => {
            this._focused = true;
            if(this._styleable)
                showMenu(this, { formats: options.formats }, this._show_toolbar);

            // TODO: replace this with a sophisticated focus system that takes into account popups and popup owners!!!
            if(current_bottom_toolbar !== this._bottom_toolbar) {
                if(current_bottom_toolbar) {
                    uiHidePopup(current_bottom_toolbar);
                    current_bottom_toolbar = undefined;
                }
                if(this._bottom_toolbar) {
                    uiShowPopup(this._bottom_toolbar, this, { position: UIRelPos.Below, offset: 12 });
                    current_bottom_toolbar = this._bottom_toolbar;
                }
            }

            if(this._showing_placeholder) {
                let p: Element;
                if(this._placeheld_value) {
                    this.root.innerHTML = this._placeheld_value;
                    p = this.root.firstElementChild || this.root;
                } else {
                    this.root.ui_empty(p = $el.DIV());
                }

                const range = document.createRange();
                range.selectNodeContents(p);
                range.collapse(true);
                const sel = window.getSelection()!;
                sel.removeAllRanges();
                sel.addRange(range);

                this._showing_placeholder = false;
            }

            this._fields?.onFocus(this);

            deduplicated_callback(ev);
        });

        el.addEventListener('blur', () => {
            this._focused = false;
            if(this._styleable)
                hideMenu(this);

            this._updatePlaceholder();

            this._fields?.onBlur(this);
        });

        if(options.fields) {
            this._fields = options.fields;
            this._fields.fixFields(this.root);
        }

        el.addEventListener('copy', copyCutHandler);
        el.addEventListener('cut', copyCutHandler);
        el.addEventListener('paste', pasteHandler);

        this._updatePlaceholder();

        onDetach(this, () => {
            if(this._bottom_toolbar && current_bottom_toolbar === this._bottom_toolbar) {
                uiHidePopup(this._bottom_toolbar);
                current_bottom_toolbar = undefined;
            }
        });
    }

    private _updatePlaceholder() {
        if(this._placeholder && !this.root.textContent && !this._showing_placeholder && !this._focused) {
            const placeholder = $el.DIV('r-placeholder', this._placeholder);
            this._placeheld_value = this.root.innerHTML;
            this.root.innerHTML = '';
            this.root.appendChild(placeholder);
            this._showing_placeholder = true;
        };
    }

    dropEnter(content: UIContent) {
        return content.type == 'files';
    }

    drop(content: UIContent) {
        if(content.type == 'files') {
            const valid_files = (content.value as File[]).filter((f: File) => isImageMime(f.type));
            if(valid_files.length == 1) {
                sUploadImage(valid_files[0], this._image_scope!).then(i => {
                    this.insertImage(i.id, Math.round(i.info.width / 2), Math.round(i.info.height / 2), i.info.width / i.info.height);
                }, err => {
                    $log('editable', 'failed', err);
                });
            }
        }
    }

    abstract value: Value;

    get text(): string {
        return this._showing_placeholder ? '' : (this.root.textContent || '');
    }

    insertImage(id: YId, height: number, width: number, aspect: number) {
        getSelection(this)?.insert(new UIImageTag(id, height, width, aspect));
        this._onInput();
    }

    insertField(field: UIValue<string>) {
        const selection = getSelection(this);
        if(!selection) return;
        selection.insert(selection.hasContent() ? new UIFieldTag(field.key, field.name) : [new UIFieldTag(field.key, field.name), ' ']);
        this._onInput();
    }

    blur() {
        this.root.blur();
    }

    focus(end?: boolean) {
        this.root.focus();

        if(end)
            select(this, 'end');
    }

    is_empty() {
        return !this.root.hasChildNodes();
    }

    get disabled() {
        return false;
    }

    set disabled(_d: boolean) {

    }

    get selection(): EDSelection | undefined {
        return this._selection;
    }

    markInvalid(_focus: boolean, _force?: boolean) {
        return false;
    }

    setParagraphStyle<P extends UIParagraphStyleParam>(param: P, value: undefined | UIParagraphStyleTypes[P]) {
        getSelection(this)?.setParagraphStyle(param, value);
    }

    getParagraphStyle<P extends UIParagraphStyleParam>(param: P) {
        return this._selection?.getParagraphStyle(param);
    }

    hasParagraphStyle<P extends UIParagraphStyleParam>(param: P, value: UIParagraphStyleTypes[P]) {
        return this._selection?.hasParagraphStyle(param, value);
    }

    setTextStyle<P extends UITextStyleParam>(style: P, value: undefined | UITextStyleTypes[P]) {
        const selection = getSelection(this);
        if(!selection) return;

        selection.setTextStyle(style, value);
        this._selection = getSelection(this);
        this._onInput();
        menu?.update();
    }

    getTextStyle<P extends UITextStyleParam>(style: P) {
        return this._selection?.getTextStyle(style);
    }

    hasTextStyle<P extends UITextStyleParam>(style: P, value: UITextStyleTypes[P]) {
        return this._selection?.hasTextStyle(style, value);
    }

    insertLink() {
        const selection = getSelection(this);
        if(!selection) return;

        const existing = selection.getContainerWithTag('A', 'object');
        if(existing) {
            selection.unwrap(existing);
            this._onInput();
            return;
        }

        if(!selection.hasContent()) {
            $log('editable', 'no content in the selection... skipping');
            return;
        }
        // TODO: i18n
        new InsertLinkDialog(this._link_values).present().then(v => {
            let elem_maker: ((content: UIViewAddSpec) => HTMLElement) | undefined

            if(v.url) {
                let url = v.url;
                if(!url.includes(':') && !url.startsWith('/') && !url.startsWith('#'))
                    url = 'https://' + url;
                elem_maker = content => $el.A(url, content, 'u-link');
            } else if(v.link) {
                elem_maker = content => {
                    const a = $el.A('', content, 'u-link');
                    a.setAttribute('data-link', v.link!);
                    return a;
                }
            }

            if(elem_maker) {
                const links = selection.wrapWith(elem_maker);
                this._onInput();
                this.focus();
                if(links.length)
                    select(links[0], 'before', 'after', links[links.length - 1]);
            }
        });
    }

    onSelectionChange() {
        this._selection = getSelection(this);
    }
}

export class EDInput extends InputBase<string> {
    constructor(options: EDInputOptions = {}) {
        super(options);
    }

    get value(): string {
        return this._getValue();
    }

    set value(v: string) {
        this._setValue(v);
    }
}

export class EDI18NSpan extends UIView {
    constructor(text: YI18NString, classes?: UIClassesSpec) {
        super('span', classes);

        this.root.ui_watch('ui-edit-language', l => {
            this.root.textContent = yGetI18NIn(text, l);
        });
    }
}

export class EDI18NInput extends InputBase<YI18NString> {
    private _value: YI18NString;
    private _language: YLanguage | undefined;

    constructor(options: EDInputOptions = {}) {
        super(options);

        this._value = {};

        this.root.ui_watch('ui-edit-language', l => {
            if(l && l != this._language) {
                if(this._language)
                    this._value[this._language] = this._getValue();
                this._language = l;
                this._setValue(this._value[l] || '');
            }
        });

        this.root.ui_listen('ui-copy-language', l => {
            this._value[l[1]] = this._value[l[0]];
            if(l[1] == this._language) {
                this._setValue(this._value[l[1]] || '');
            }
        });

        this.root.ui_listen('ui-translate-language', l => {
            const s = this._value[l[0]];
            if(!s) return;
            const src = l[0];
            const dst = l[1];
            uTranslateText(s, src, dst, true).then(r => {
                this._value[dst] = r.text;
                if(dst == this._language) {
                    this._setValue(r.text || '');
                }
            });
        });
    }

    get value(): YI18NString {
        if(this._language)
            this._value[this._language] = this._getValue();

        return this._value;
    }

    set value(v: YI18NString | undefined) {
        this._value = v ? yCopy(v) : {};

        for(const l in this._value) {
            if(!$vars.supported_languages.includes(l as YLanguage))
                delete this._value[l as YLanguage];
        }

        if(this._language)
            this._setValue(this._value[this._language] || '');
    }
}
